<template>
  <v-row no-gutters class="fill-height">
    <v-col cols="12">
      <card-title-row class="pl-0 pt-5" translation-path="sidebar.sewerDataProfile.title" :has-buttons="false" />
      <sidebar-row class="pt-5">
        <v-col cols="8" class="px-0 pb-7">
          <v-tooltip bottom max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <data-input v-model="currentPipesLayer" v-bind="currentPipesInput" />
              </span>
            </template>
            {{ getLayerTooltip(currentPipesLayer ? currentPipesLayer.name : '') }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <sidebar-row>
        <v-tooltip bottom :disabled="sewerPipesDataSourceAdded" max-width="300">
          <template #activator="{ on }">
            <span v-on="on">
              <base-button-subheader subheader-translation-path="sidebar.sewerProfile.selectLinksSubtitle">
                <dots-menu
                  :items="dotsItems"
                  @single="turnOnSelecting('single')"
                  @multiple="turnOnSelecting('multiple')"
                  @freehand="turnOnSelecting('freehand')"
                >
                  <template #activator="{ on }">
                    <v-badge :content="selectedLinksLength" :value="isAnyLinkSelected" color="error" overlap>
                      <base-button
                        v-on="getButtonListeners(on)"
                        @click="turnOffSelecting"
                        class="mr-0"
                        translation-path="sidebar.sewerProfile.selectLinks"
                        :button-style="buttonStyle"
                        :disabled="!sewerPipesDataSourceAdded"
                      >
                      </base-button>
                    </v-badge>
                  </template>
                </dots-menu>
              </base-button-subheader>
            </span>
          </template>
          {{ $i18n.t('snackbar.modulesNotConfigurate.sewerDataProfile') }}
        </v-tooltip>
      </sidebar-row>
      <template v-if="isAnyLinkSelected">
        <sidebar-row class="pt-5 pr-5">
          <v-row no-gutters class="justify-end">
            <v-col cols="auto">
              <clickable-text @click="deleteFeature(null)">
                {{ $i18n.t('sidebar.sewerProfile.deleteAllLinks') }}
              </clickable-text>
            </v-col>
          </v-row>
        </sidebar-row>
        <sidebar-row class="pr-5">
          <v-list class="py-0">
            <template v-for="feature in selectedLinks">
              <v-list-item
                class="hover-list-element"
                :key="feature"
                @mouseleave="onHover({ id: feature, layerId: currentPipesLayerId }, false)"
                @mouseenter="onHover({ id: feature, layerId: currentPipesLayerId }, true)"
              >
                <v-list-item-content class="pa-0">
                  <v-list-item-title>
                    <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                      ID: {{ feature }}
                    </v-subheader>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon @click="deleteFeature(feature)" color="rgba(0,0,0,.7)" size="19px">mdi-close</v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </sidebar-row>
        <sidebar-row class="pt-5" slot-cols="auto">
          <base-button
            class="mr-0"
            translation-path="sidebar.sewerProfile.generateProfile"
            @click="onOpened"
            :loading="isLoading"
          >
          </base-button>
        </sidebar-row>
      </template>
      <data-profile-dialog
        v-if="isDialogLoaded"
        :is-visible.sync="isDialogVisible"
        :image="image"
        :image-name="imageName"
      />
    </v-col>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';

import identificationSidebarCard from '@/mixins/identificationSidebarCard';

import CardTitleRow from '@/components/CardTitleRow';
import ClickableText from '@/components/ClickableText';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'SewerDataProfile',
  mixins: [identificationSidebarCard],
  components: {
    CardTitleRow,
    ClickableText,
    DataInput,
    DotsMenu,
    DataProfileDialog: () => import('@/components/DataProfileDialog'),
    SidebarRow,
  },
  computed: {
    activeTool: get('tools/activeTool'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    layerMetadata: get('layers/metadata'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    selectedLinks: get('map/selectedFeatures@:currentPipesLayerId'),
    sewerPipesGeomName() {
      return this.layerMetadata[this.sewerPipesDataSource].attributes_schema.geometry_name;
    },
    buttonStyle() {
      return this.isButtonActive
        ? {
            backgroundColor: `${this.$_colors.primarybackground} !important`,
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    computedSelectedLinks() {
      return this.selectedLinks || [];
    },
    currentPipesInput() {
      return {
        dataType: { name: 'select' },
        disabled: !this.sewerPipesDataSourceAdded,
        hideDetails: true,
        items: this.sewerPipesLayers,
        itemText: 'name',
        label: this.$i18n.t('sidebar.sewerDataProfile.sewerPipesLayer'),
        returnObject: true,
      };
    },
    currentPipesLayerId() {
      return this.currentPipesLayer?.id;
    },
    dotsItems() {
      return [
        {
          name: 'single',
        },
        {
          name: 'multiple',
          disabled: !this.isFetched || !this.mapPanelDataFetched,
        },
        {
          name: 'freehand',
          disabled: !this.isFetched || !this.mapPanelDataFetched,
        },
      ];
    },
    isAnyLinkSelected() {
      return this.selectedLinksLength > 0;
    },
    selectedLinksLength() {
      return this.computedSelectedLinks.length;
    },
    sewerPipesDataSource: get('admin/modulesMapping@sewer.sewer_pipes_data_source'),
    sewerPipesDataSourceAdded() {
      return this.sewerPipesLayers?.length > 0;
    },
    sewerPipesLayers() {
      // Force update to trigger tooltips
      this.$forceUpdate();
      return this.projectLayers.filter(layer => layer.data_source_name === this.sewerPipesDataSource);
    },
  },
  data: () => ({
    currentPipesLayer: undefined,
    isButtonActive: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isFetched: false,
    isLoading: false,
    image: undefined,
    imageName: undefined,
  }),
  methods: {
    getLayerFeatures: call('layers/getLayerFeatures'),
    getProfile: call('modeling/getSewerDataProfile'),
    async getIntersectingLinks(geometry) {
      const r = await this.getLayerFeatures({
        features_filter: {
          '%SPATIALLY_INTERSECTS': {
            [`${this.sewerPipesDataSource}.${this.sewerPipesGeomName}`]: [geometry],
          },
        },
        layer_id: this.currentPipesLayerId,
        params: {
          ids_only: true,
        },
      });
      return r;
    },
    async init() {
      this.isFetched = false;
      this.currentPipesLayer = this.sewerPipesLayers[0];
      this.isFetched = true;
    },
    async onOpened() {
      try {
        this.isLoading = true;
        const payload = {
          pipes: this.selectedLinks,
        };
        const r = await this.getProfile(payload);
        this.imageName = this.$_extractFilename(r.headers);
        this.image = this.$_createImageFromArrayBuffer(r.data, r.headers);
        this.openProfileDialog();
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }
    },
    clearSelection(layerId) {
      this.turnOffSelecting();
      if (layerId || layerId == '0') {
        this.deleteFeature(null, layerId);
      } else {
        this.deleteFeature(null);
      }
    },
    deleteFeature(id, layerId = this.currentPipesLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'highlitedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    getButtonListeners(on) {
      return this.isButtonActive
        ? {
            click: this.turnOffSelecting,
          }
        : on;
    },
    openProfileDialog() {
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    turnOnSelecting(type) {
      if (type === 'single') {
        this.$root.$emit('attachOnClickSelection', this.currentPipesLayerId, false);
      } else if (type === 'multiple') {
        this.$root.$emit('selection-action', 'multiple', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: this.currentPipesLayerId,
        });
      } else {
        this.$root.$emit('selection-action', 'freehand', {
          selectionBoxAction: this.getIntersectingLinks,
          layerId: this.currentPipesLayerId,
        });
      }
      this.isButtonActive = true;
    },
    turnOffSelecting() {
      if (this.activeTool !== 'selection') {
        return;
      }
      this.$root.$emit('selection-action');
      this.isButtonActive = false;
    },
    getLayerTooltip(name) {
      return this.sewerPipesDataSourceAdded ? name : this.$i18n.t('snackbar.modulesNotConfigurate.sewerDataProfile');
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isButtonActive = false;
      }
    },
    currentPipesLayerId(nV, oV) {
      this.clearSelection(oV);
    },
    sewerPipesLayers: {
      deep: true,
      immediate: true,
      handler(nV) {
        if (nV.find(layer => layer.id === this.currentPipesLayerId)) {
          return;
        } else {
          this.currentPipesLayer = nV[0];
        }
      },
    },
  },
  beforeDestroy() {
    this.clearSelection();
  },
  mounted() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep .flex-wrap {
  flex-wrap: nowrap !important;
}
</style>
